const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const isLocalDevelopment =
  typeof window !== 'undefined' && LOCAL_HOSTS.includes(window.location.hostname);

const API_URL = isLocalDevelopment
  ? 'http://localhost:8001'
  : process.env.REACT_APP_BACKEND_URL;

const readJsonResponse = async (response) => {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(
      response.ok
        ? 'استجابة خادم الإدارة غير صالحة. تحقق من إعدادات خادم الخلفية.'
        : `تعذر الاتصال بخدمة الإدارة (${response.status}).`
    );
  }
};

export const adminAuthService = {
  async login(email, password, remember = false) {
    try {
      const response = await fetch(`${API_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, remember }),
      });

      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.detail || 'فشل تسجيل الدخول');
      }

      const authData = data.data;
      if (!authData?.user || !authData?.token) {
        throw new Error('Unexpected login response from server');
      }

      const internalRoles = ['admin', 'manager', 'lawyer', 'secretary', 'reviewer', 'auditor', 'intake', 'reception', 'intern', 'employee'];
      const isInternalRole = internalRoles.some((role) => `${authData.user.role || ''}`.toLowerCase().includes(role));
      if (!isInternalRole) {
        throw new Error('هذا الحساب ليس حسابا إداريا');
      }

      return {
        user: {
          id: authData.user.id,
          email: authData.user.email,
          fullName: authData.user.fullName || authData.user.full_name,
          role: authData.user.role,
          roleName: authData.user.roleName || 'مدير النظام',
          permissions: authData.user.permissions || [],
        },
        token: authData.token,
      };
    } catch (error) {
      console.error('Login error:', error);
      if (error instanceof TypeError && isLocalDevelopment) {
        throw new Error('تعذر الوصول إلى خادم الإدارة المحلي. يرجى تشغيل backend على المنفذ 8001 ثم المحاولة مجدداً.');
      }
      throw error;
    }
  },

  async logout(token) {
    try {
      const response = await fetch(`${API_URL}/api/admin/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error('Logout failed');
      }

      return true;
    } catch (error) {
      console.error('Logout error:', error);
      return false;
    }
  },

  async changePassword(token, oldPassword, newPassword) {
    try {
      const response = await fetch(`${API_URL}/api/admin/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ oldPassword, newPassword }),
      });

      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.detail || 'فشل تغيير كلمة المرور');
      }

      return data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  },
};

export const adminDashboardService = {
  async getStats(token) {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.detail || 'فشل جلب الإحصائيات');
      }
      return data.data;
    } catch (error) {
      console.error('Get stats error:', error);
      throw error;
    }
  },
};

export const adminRolesService = {
  async seedRoles(token) {
    try {
      const response = await fetch(`${API_URL}/api/admin/roles/seed`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.detail || 'فشل تهيئة الأدوار');
      }

      return data;
    } catch (error) {
      console.error('Seed roles error:', error);
      throw error;
    }
  },

  async getAllRoles(token) {
    try {
      const response = await fetch(`${API_URL}/api/admin/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.detail || 'فشل جلب الأدوار');
      }
      return data.data;
    } catch (error) {
      console.error('Get roles error:', error);
      throw error;
    }
  },
};
